<template>
  <div
    ref="containerElement"
    class="lottie-animation"
  >
    <PlayPauseButton
      v-model="isPlaying"
      :aria-label="isPlaying ? 'Animation pausieren' : 'Animation abspielen'"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, withDefaults, onMounted } from 'vue';
import lottie, { AnimationItem } from 'lottie-web';
import useScrollAnimation from '@/components/anker/animations/scroll-animation';
import { PlayPauseButton, reducedMotion } from '@i22-td-smarthome/component-library';
import { watch } from '#imports';

export type LottieAnimationProps = {
  path: string;
  mode?: 'playOnViewPort' | 'scrollTimeline';
  scrollPosStart?: 'top bottom' | string;
  scrollPosEnd?: 'bottom top' | string;
};

const props = withDefaults(defineProps<LottieAnimationProps>(), {
  mode: 'playOnViewPort',
  scrollPosStart: 'top bottom',
  scrollPosEnd: 'bottom top',
});

const isReducedMotion = reducedMotion.useReducedMotion();

const isPlaying = ref(false);
const animation = ref<AnimationItem | undefined>(undefined);
const containerElement = ref<HTMLDivElement | null>(null);

const pauseAnimation = () => {
  if (animation.value) {
    animation.value.pause();
  }
};
const playAnimation = () => {
  if (animation.value) {
    animation.value.play();
  }
};

watch(isPlaying, (val) => {
  if (val) {
    playAnimation();
  } else {
    pauseAnimation();
  }
});

const { createScrollTrigger } = useScrollAnimation();

const initLottiefile = () => {
  if (props.mode === 'playOnViewPort') {
    animation.value = lottie.loadAnimation({
      container: containerElement.value,
      renderer: 'svg',
      loop: true,
      autoplay: false,
      path: props.path,
    });
    createScrollTrigger({
      trigger: containerElement.value,
      scrub: true,
      start: props.scrollPosStart,
      end: props.scrollPosEnd,
      markers: false,
      onToggle: (self) => {
        if (self.isActive && isPlaying.value) {
          animation.value.play();
        } else {
          animation.value.stop();
        }
      },
    });
  }

  if (props.mode === 'scrollTimeline') {
    animation.value = lottie.loadAnimation({
      container: containerElement.value,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      path: props.path,
    });
    createScrollTrigger({
      trigger: containerElement.value,
      scrub: true,
      start: props.scrollPosStart,
      end: props.scrollPosEnd,
      markers: false,
      onUpdate: (self) => {
        animation.value.goToAndStop(self.progress * (animation.value.totalFrames - 1), true);
      },
    });
  }
};

onMounted(() => {
  initLottiefile();

  setTimeout(() => {
    if (!isReducedMotion.value) {
      isPlaying.value = true;
    }
  }, 10);
});

defineExpose({ initLottiefile });
</script>

<style scoped lang="scss">
.lottie-animation {
  position: relative;
}
</style>
